import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Loader2, Sparkles, ChromeIcon } from "lucide-react";
import { PLATFORM_DOMAIN } from "@/lib/tenantHost";
import GoogleLoginButton from "@/lib/GoogleLoginButton";
import CodeField from "./CodeField";
import { OPERATING_CURRENCIES, currencyLabel } from "../../lib/currencies";
import { knownCodeMessage } from "../../lib/apiErrors";

// THE OPERATING CURRENCIES, and the practice charges its clients in the one it picks
// here for as long as it exists — the field locks the moment it prices or sells anything
// (`platform_helpers.set_tenant_currency`). EUR and INR were offered here for years with
// no payout path out of the platform's balance behind either, so a practice that chose
// one could be billed and could never be paid. The codes are the shared list
// (lib/currencies.js); this table only names the country each one is for.
const CURRENCY_COUNTRY = { usd: "USA", cad: "Canada" };

// The two platform-code slots of a signup (contract §5.2: `promo_code`, `gift_card_code`).
const OFFER_KINDS = ["promo", "giftcard"];
const NO_OFFERS = { promo: null, giftcard: null };

/**
 * ONE entry field for both slots. The server decides a code's kind (`GC-` prefix = gift
 * card), so the visitor types whatever they were given and the applied preview is routed
 * into the slot its `kind` names; each applied code then shows as its own row with its
 * own Remove. The entry field narrows to the slot still open, and disappears once both are
 * filled. Its testid scope is that of the first open slot.
 */
function SignupCodes({ offers, onOfferApplied, onOfferRemoved, initialCode, form, disabled, t }) {
  const params = { plan_id: form.plan_id, currency: form.currency, billing_cycle: form.billing_cycle };
  const open = OFFER_KINDS.filter((k) => !offers[k]);
  return (
    <div className="space-y-3">
      {OFFER_KINDS.filter((k) => offers[k]).map((k) => (
        <CodeField
          key={k}
          scope={`signup-${k}`}
          value={offers[k]}
          params={params}
          disabled={disabled}
          onApplied={onOfferApplied}
          onRemoved={() => onOfferRemoved(k)}
          tone="dark"
        />
      ))}
      {open.length > 0 && (
        <CodeField
          // A new slot set is a new field: typed text and a stale error do not carry over.
          key={open.join(",")}
          scope={`signup-${open[0]}`}
          value={null}
          initialCode={initialCode}
          params={params}
          allowedKinds={open}
          refusedKindText={{
            promo: t("signup.code.promoAlreadyApplied"),
            giftcard: t("signup.code.giftcardAlreadyApplied"),
          }}
          disabled={disabled}
          onApplied={onOfferApplied}
          tone="dark"
        />
      )}
      <p className="text-xs text-white/40" data-testid="signup-code-hint">{t("signup.code.hint")}</p>
    </div>
  );
}

const BILLING_OPTIONS = [
  { key: "monthly", i18nKey: "signup.billingMonthly" },
  { key: "annual", i18nKey: "signup.billingAnnual" },
];

export const SignupForm = ({
  form,
  setForm,
  handleSlug,
  submit,
  submitting,
  acceptedTerms,
  setAcceptedTerms,
  config,
  plans,
  plansFailed = false,
  onRetryPlans,
  onGoogleSignedIn,
  user,
  t,
  tc,
  slugAvailable,
  offers = NO_OFFERS,
  onOfferApplied,
  onOfferRemoved,
  initialCode = "",
}) => (
  <div className="lg:col-span-3">
    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sol-gold/10 border border-sol-gold/30 text-xs uppercase tracking-widest text-sol-gold mb-8">
      <Sparkles className="w-3 h-3" /> 1 / 2
    </div>
    <h1 className="font-sol font-semibold text-4xl lg:text-5xl leading-tight mb-4">{t("signup.title")}</h1>
    {/* `config.trialPeriodDays` is `null` until `GET /saas/config` resolves (or if the
        server ever omits/mistypes the field) — the digit-free generic sentence covers
        that instead of interpolating "undefined-day trial" on first paint. */}
    <p className="text-white/60 mb-12">
      {config.trialPeriodDays != null
        ? t("signup.subtitle", { days: config.trialPeriodDays })
        : t("pricing.subtitle")}
    </p>

    {config.stripeDemoMode && (
      <div className="mb-8 p-4 rounded-2xl bg-sol-gold/10 border border-sol-gold/30 text-sm">
        <strong className="text-sol-gold">{t("signup.demoModeActive")}</strong> — {t("signup.demoModeBody")}
      </div>
    )}

    <form onSubmit={submit} className="space-y-6" data-testid="signup-form">
      {user?.email ? (
        <div data-testid="signup-google-connected" className="flex items-center gap-3 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-400/30 text-sm">
          <ChromeIcon className="w-4 h-4 text-emerald-300" />
          <span className="text-white/80">{t("signup.googleConnected")} <strong className="text-white">{user.email}</strong></span>
        </div>
      ) : (
        <div>
          <div data-testid="signup-google-btn">
            <GoogleLoginButton onSuccess={onGoogleSignedIn} />
          </div>
          <div className="flex items-center gap-4 my-2">
            <span className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-white/40 uppercase tracking-widest">{t("signup.or")}</span>
            <span className="flex-1 h-px bg-white/10" />
          </div>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-white/70 mb-2 block">{tc("common.name")}</Label>
          <Input
            data-testid="signup-owner-name"
            value={form.owner_name}
            onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
            placeholder={t("signup.ownerNamePlaceholder")}
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
          />
        </div>
        <div>
          <Label className="text-white/70 mb-2 block">{tc("common.email")} *</Label>
          <Input
            data-testid="signup-email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="you@example.com"
            disabled={Boolean(user?.email)}
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12 disabled:opacity-60"
          />
          <p className="mt-1.5 text-xs text-white/40">
            {user?.email
              ? t("signup.emailHintGoogle")
              : t("signup.emailHintManual")}
          </p>
        </div>
      </div>

      <div>
        <Label className="text-white/70 mb-2 block">{t("signup.practiceName")}</Label>
        <Input
          data-testid="signup-practice-name"
          maxLength={120}
          value={form.practice_name}
          onChange={(e) => setForm({ ...form, practice_name: e.target.value })}
          placeholder={t("signup.practiceNamePlaceholder")}
          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl h-12"
        />
        <p className="mt-2 text-xs text-white/40" data-testid="signup-practice-name-hint">
          {t("signup.practiceNameOptionalHint")}
        </p>
      </div>

      <div>
        <Label className="text-white/70 mb-2 block">{t("signup.slug")} *</Label>
        <div className="flex items-center bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <Input
            data-testid="signup-slug"
            required
            value={form.slug}
            onChange={(e) => handleSlug(e.target.value)}
            placeholder="mypractice"
            className="bg-transparent border-0 text-white placeholder:text-white/30 h-12 flex-1 focus-visible:ring-0"
          />
          <span className="px-4 text-white/50 text-sm whitespace-nowrap border-l border-white/10" data-testid="signup-slug-suffix">.{PLATFORM_DOMAIN}</span>
        </div>
        <p className="mt-2 text-xs text-white/40" data-testid="signup-slug-hint">{t("signup.slugHint")}</p>
        {slugAvailable && (
          <p
            className={`mt-1 text-xs ${slugAvailable.available ? "text-emerald-400" : "text-red-400"}`}
            data-testid="signup-slug-availability"
          >
            {/* A coded refusal (a retired address) is worded in the reader's language; the
                server's `reason` is shown only for refusals that carry no code. */}
            {slugAvailable.available
              ? t("signup.slugAvailable")
              : knownCodeMessage(slugAvailable.code, tc) ?? slugAvailable.reason}
          </p>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-white/70 mb-2 block">{t("signup.currency")}</Label>
          <select
            data-testid="signup-currency"
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-3 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
          >
            {OPERATING_CURRENCIES.map((c) => (
              <option key={c} value={c} className="bg-sol-midnight">
                {CURRENCY_COUNTRY[c] ? `${CURRENCY_COUNTRY[c]} (${currencyLabel(c)})` : currencyLabel(c)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-white/70 mb-2 block">{t("signup.plan")}</Label>
          <select
            data-testid="signup-plan"
            value={form.plan_id}
            onChange={(e) => setForm({ ...form, plan_id: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-xl h-12 px-3 text-white focus:outline-none focus:ring-2 focus:ring-amber-300/40"
          >
            {plans.map((p) => (
              <option key={p.plan_id} value={p.plan_id} className="bg-sol-midnight">
                {`${p.name} – ${p.prices?.[form.currency] || 0} ${form.currency}${t("pricing.perMonth")}`}
              </option>
            ))}
          </select>
          {plansFailed && (
            <p role="alert" data-testid="signup-plans-error" className="mt-2 text-sm text-rose-300">
              {t("signup.plansLoadFailed")}{" "}
              <button type="button" onClick={onRetryPlans} data-testid="signup-plans-retry"
                      className="underline underline-offset-2 hover:text-white">
                {t("signup.plansRetry")}
              </button>
            </p>
          )}
        </div>
      </div>

      <div>
        <Label className="text-white/70 mb-2 block">{t("signup.billingLabel")}</Label>
        <div className="flex gap-2 p-1 rounded-xl bg-white/5 border border-white/10" data-testid="signup-billing-toggle">
          {BILLING_OPTIONS.map((b) => (
            <button
              key={b.key}
              type="button"
              onClick={() => setForm({ ...form, billing_cycle: b.key })}
              data-testid={`signup-billing-${b.key}`}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                form.billing_cycle === b.key ? "bg-sol-gold text-black" : "text-white/60 hover:text-white"
              }`}
            >
              {t(b.i18nKey)}
            </button>
          ))}
        </div>
      </div>

      <SignupCodes
        offers={offers}
        onOfferApplied={onOfferApplied}
        onOfferRemoved={onOfferRemoved}
        initialCode={initialCode}
        form={form}
        disabled={submitting}
        t={t}
      />

      <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="signup-terms-label">
        <input
          type="checkbox"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
          data-testid="signup-terms-checkbox"
          className="mt-1 w-4 h-4 rounded border-white/30 bg-white/5 accent-sol-gold flex-shrink-0"
        />
        <span className="text-sm text-white/60 leading-relaxed">
          {t("signup.acceptTermsPrefix")}{" "}
          <Link to="/legal/terms" target="_blank" className="text-sol-gold hover:underline" data-testid="signup-terms-link">{t("footer.terms")}</Link>{" "}
          {t("signup.acceptTermsMiddle")}{" "}
          <Link to="/legal/privacy" target="_blank" className="text-sol-gold hover:underline" data-testid="signup-privacy-link">{t("footer.privacy")}</Link>.
        </span>
      </label>

      <Button
        type="submit"
        disabled={submitting || !acceptedTerms || slugAvailable?.available === false}
        className="w-full bg-sol-gold hover:bg-sol-gold/90 text-black rounded-full py-7 text-base font-medium disabled:opacity-50"
      >
        {submitting ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            {t("signup.submit")} <ArrowRight className="w-4 h-4 ml-2" />
          </>
        )}
      </Button>
    </form>
  </div>
);

export default SignupForm;
